import { Card, CardContent, styled } from '@mui/material'
import Icon from '@mui/material/Icon'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import { useEffect, useMemo, useState } from 'react'

import Loader from '@/app/component/atom/loader'
import TabHeader from '@/app/component/atom/tab-header'
import { useGetWaTemplateRefresh } from '@/app/module/content/hooks/use-get-wa-template-refresh'
import { useGetWaTemplates } from '@/app/module/content/hooks/use-get-wa-templates'

import WATemplatesTable from './table'
import WATemplatesFilter from './templates-filter'

export default function WATemplates() {
  const { data, isFetching, refetch } = useGetWaTemplates({
    register: true,
  })
  const [, { isLoading }] = useGetWaTemplateRefresh()

  // Get unique categories from templates
  const availableCategories = useMemo(() => {
    const templates = data?.templates || []
    const categoriesMap = new Map<string, boolean>()
    templates.forEach((template) => {
      const category = template.category || 'Unknown'
      if (!categoriesMap.has(category)) {
        categoriesMap.set(category, true)
      }
    })
    return Array.from(categoriesMap.keys()).sort()
  }, [data?.templates])

  const [selectedCategories, setSelectedCategories] = useState<string[]>([])
  const [initialized, setInitialized] = useState(false)

  useEffect(() => {
    if (initialized || !availableCategories.length) {
      return
    }

    // set default selected categories to all available categories
    setSelectedCategories(availableCategories)
    setInitialized(true)
  }, [availableCategories, initialized])

  const handleCategoriesChange = (categories: string[]) => {
    setSelectedCategories(categories)
  }

  const loading = isFetching || isLoading

  return (
    <Container>
      <TabHeader
        title="WhatsApp Templates"
        action={
          <HeaderActions>
            <WATemplatesFilter
              categories={availableCategories}
              selectedCategories={selectedCategories}
              onCategoriesChange={handleCategoriesChange}
            />
            <Tooltip id="reload-wa-templates" title="Reload WhatsApp Templates">
              <IconButton onClick={refetch}>
                <Icon>refresh</Icon>
              </IconButton>
            </Tooltip>
          </HeaderActions>
        }
      />
      <ContentContainer>
        <WATemplatesTable templates={data?.templates || []} selectedCategories={selectedCategories} />
      </ContentContainer>

      {loading && <Loader />}
    </Container>
  )
}

const Container = styled(Card)({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  height: '100%',
  maxHeight: '100%',
})

const ContentContainer = styled(CardContent)({
  display: 'flex',
  flexDirection: 'column',
  height: 'calc(100% - 64px)', // Adjust height to account for header
})

const HeaderActions = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
})
